"use client";

import { useState, useEffect } from "react";
import { Icon, IconName } from "@zapier/zinnia-icons";
import { Divider, Spacer, Text } from "@zapier/design-system";
import styles from "./TemplateLibraryPage.module.css";
import { BreadCrumbItem } from "../BreadCrumbs/BreadCrumbs";
import { Hero } from "./Hero";
import { ServiceRoute } from "src/utils/routes";
import { Category } from "src/server/zsl/templates/types";
import { Sidebar } from "../Sidebar/Sidebar";
import { TemplatesGrid } from "./TemplatesGrid";
import { type Template } from "@zapier/templates";
import { useSearchParams } from "next/navigation";
import { getGalleryTemplates } from "src/lib/actions";
import { CustomPageData } from "../TemplateLibraryLanding/TemplateLibraryLanding";

interface TemplateLibraryLandingProps {
  title: string;
  description?: string;
  image?: string;
  iconName?: IconName;
  crumbs: BreadCrumbItem[];
  templates: Template[];
  showcaseTemplate?: Template;
  categories: Category[];
  productsMenu?: ServiceRoute[];
  isLoggedIn: boolean;
  customPageData?: CustomPageData; // Optional custom page data
  showUnifiedTemplatesPage: boolean;
}

export const TemplateLibraryPage = ({
  title,
  description,
  image,
  iconName,
  crumbs,
  templates: initialTemplates = [],
  showcaseTemplate,
  categories,
  productsMenu,
  isLoggedIn,
  customPageData,
  showUnifiedTemplatesPage,
}: TemplateLibraryLandingProps) => {
  // Determine if this is a custom category page (has navigation data)
  const isCustomPage = !!customPageData;
  const searchParams = useSearchParams();

  const filterCategory = searchParams?.get("category");
  const filterApp = searchParams?.get("app");
  const hasNavigationFilters = filterCategory || filterApp;

  const [templates, setTemplates] = useState<Template[]>(
    isCustomPage && hasNavigationFilters ? [] : initialTemplates,
  );

  useEffect(() => {
    // Dynamic filtering for custom category pages
    if (isCustomPage) {
      const updateFilteredTemplates = async () => {
        const galleryTemplatesResponse = await getGalleryTemplates({
          variant: null,
          draftMode: customPageData.isDraftModeEnabled,
          sourceCategories: customPageData.sourceCategories,
          navigationCategories: filterCategory ? [filterCategory] : undefined,
          navigationApps: filterApp ? [filterApp] : undefined,
          skip: 0,
          limit: 27,
          showUnifiedTemplatesPage,
        });

        setTemplates(galleryTemplatesResponse.templates);
      };

      updateFilteredTemplates();
    }
  }, [filterCategory, filterApp, isCustomPage]);

  return (
    <>
      <Hero
        title={title}
        description={description}
        crumbs={crumbs}
        image={image}
        iconName={iconName}
        isUnifiedTemplatesPage={showUnifiedTemplatesPage}
      />
      <div className={styles.container}>
        <div className={styles.mobileMenuContainer}>
          <details className={styles.mobileMenu}>
            <summary className={styles.mobileMenuToggle}>
              <Text type="SmallPrint2">Menu</Text>
              <div className={styles.mobileMenuIcon}>
                <Icon name="arrowSmallDown" size={20} isBlock />
              </div>
            </summary>
            <Sidebar
              categories={
                isCustomPage && customPageData
                  ? customPageData.navigationCategories
                  : categories
              }
              navigationApps={customPageData?.navigationApps || []}
              productsMenu={isCustomPage ? [] : productsMenu}
              includeHomepage
              isCustomPage={isCustomPage}
            />
            <Spacer height={12} />
            <Divider height={1} />
            <Spacer height={20} />
          </details>
        </div>
        <div className={styles.sidebarContainer}>
          {
            <Sidebar
              categories={
                isCustomPage && customPageData
                  ? customPageData.navigationCategories
                  : categories
              }
              navigationApps={customPageData?.navigationApps || []}
              productsMenu={isCustomPage ? [] : productsMenu}
              includeHomepage={isCustomPage}
              isCustomPage={isCustomPage}
            />
          }
        </div>
        <TemplatesGrid
          title={title}
          templates={templates}
          showcaseTemplate={showcaseTemplate}
          isLoggedIn={isLoggedIn}
          isUnifiedTemplatesPage={showUnifiedTemplatesPage}
        />
      </div>
    </>
  );
};
